// CartHub Dashboard — Stats Component.
// KPI, trend, funnel, email performance, top prodotti, attività, insight AI.

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { api, Ic } from "../shared";
import { dateLocaleForAppLang, formatMoney } from "../i18n";

export default function Stats({ shopId, currencyCode = "EUR" }) {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation("stats");
  const dateLoc = dateLocaleForAppLang(i18n.language);
  const eur = (n) => formatMoney(i18n.language, currencyCode, n);

  useEffect(() => {
    if (!shopId) return;
    api(`/stats/${shopId}`)
      .then((d) => {
        setS(d);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [shopId]);

  if (loading)
    return (
      <div className="empty">
        <div className="empty-icon-svg" aria-hidden>
          {Ic.loader}
        </div>
        <div className="empty-title">{t("loading")}</div>
      </div>
    );
  if (!s || s.error) return <div className="ch-info">{t("errorLoad")}</div>;

  const trend = s.trend || [];
  const tMax = Math.max(...trend.map((x) => parseInt(x.abandoned, 10) || 0), 1);
  const funnel = s.funnel || {};
  const fSteps = [
    { l: t("funnel.abandoned"), v: funnel.abandoned || 0, color: "#9ca3af" },
    { l: t("funnel.emailsSent"), v: funnel.got_email || 0, color: "#0A85CF" },
    { l: t("funnel.opened"), v: funnel.opened || 0, color: "#f59e0b", note: t("funnel.openedNote") },
    { l: t("funnel.clicked"), v: funnel.clicked || 0, color: "#10b981", note: t("funnel.clickedNote") },
    { l: t("funnel.recovered"), v: funnel.recovered || 0, color: "#2e7d32" },
  ];
  const fMax = Math.max(funnel.abandoned || 1, 1);
  const actIcons = { email: Ic.mail, recovered: Ic.check, recovered_popup: Ic.popup };

  const kpiCards = [
    {
      k: "rr",
      l: t("kpi.recoveryRate"),
      v: `${s.recovery_rate || 0}%`,
      sub: t("kpi.recoveryRateSub", { recovered: s.recovered_carts || 0, total: s.total_carts || 0 }),
      accent: true,
      up: true,
    },
    {
      k: "rev",
      l: t("kpi.revenueRecovered"),
      v: eur(s.revenue_recovered),
      sub: t("kpi.revenueRecoveredSub"),
      up: true,
    },
    {
      k: "em",
      l: t("kpi.emailsSent"),
      v: s.emails_sent_total || 0,
      sub: t("kpi.emailsSentSub"),
    },
    {
      k: "avg",
      l: t("kpi.avgRecovered"),
      v: eur(s.avg_recovered_value),
      sub: t("kpi.avgRecoveredSub"),
    },
  ];

  return (
    <div>
      <div className="stats-grid">
        {kpiCards.map((c) => (
          <div key={c.k} className="stat-card">
            <div className="stat-label">{c.l}</div>
            <div className={`stat-val${c.accent ? " accent" : ""}`}>{c.v}</div>
            <div className={`stat-sub${c.up ? " up" : ""}`}>{c.sub}</div>
          </div>
        ))}
      </div>

      <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 20px", lineHeight: 1.55 }}>{t("statsPage.popupStatsLead")}</p>

      <div className="grid-2">
        <div className="ch-card">
          <h2>{t("trend.title")}</h2>
          <p>{t("trend.subtitle")}</p>
          {trend.length > 0 ? (
            <>
              <div style={{ display: "flex", gap: 4, alignItems: "flex-end", height: 80, marginBottom: 8 }}>
                {trend.map((row, i) => {
                  const ab = parseInt(row.abandoned, 10) || 0;
                  const rec = parseInt(row.recovered, 10) || 0;
                  return (
                    <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2, position: "relative" }}>
                      <div style={{ width: "100%", height: `${(ab / tMax) * 68}px`, background: "#e5e7eb", borderRadius: "2px 2px 0 0", position: "relative" }}>
                        {rec > 0 && (
                          <div
                            style={{
                              position: "absolute",
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: `${(rec / ab) * 100}%`,
                              background: "#2e7d32",
                              borderRadius: "2px 2px 0 0",
                            }}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#9ca3af", marginBottom: 12 }}>
                <span>{trend[0]?.date}</span>
                <span>{trend[trend.length - 1]?.date}</span>
              </div>
              <div style={{ display: "flex", gap: 16, fontSize: 13 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ width: 10, height: 10, background: "#e5e7eb", borderRadius: 2, display: "inline-block" }} />
                  {t("trend.legendAbandoned")}
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ width: 10, height: 10, background: "#2e7d32", borderRadius: 2, display: "inline-block" }} />
                  {t("trend.legendRecovered")}
                </span>
              </div>
            </>
          ) : (
            <div className="ch-info">{t("trend.noData")}</div>
          )}
        </div>
        <div className="ch-card">
          <h2>{t("funnel.title")}</h2>
          <p>{t("funnel.subtitle")}</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {fSteps.map((step, i) => {
              const pct = fMax > 0 ? Math.round((step.v / fMax) * 100) : 0;
              return (
                <div key={i}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 3 }}>
                    <span style={{ color: "#6b7280" }}>
                      {step.l}
                      {step.note && (
                        <span style={{ color: "#9ca3af", fontSize: 11, marginLeft: 4 }}>({step.note})</span>
                      )}
                    </span>
                    <span style={{ fontWeight: 600, color: step.color }}>{step.v}</span>
                  </div>
                  <div style={{ height: 8, background: "#f3f4f6", borderRadius: 4 }}>
                    <div style={{ height: "100%", width: `${pct}%`, background: step.color, borderRadius: 4, transition: "width 0.3s" }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div
            style={{
              marginTop: 12,
              paddingTop: 12,
              borderTop: "1px solid #e5e5e5",
              fontSize: 13,
              color: "#6b7280",
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <div>
              {t("funnel.revenueAtRisk")}{" "}
              <strong style={{ color: "#0A85CF" }}>{eur(s.revenue_at_risk)}</strong>
              <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: 4 }}>{t("funnel.revenueAtRiskHint")}</span>
            </div>
            <div>
              {t("funnel.revenueRecoverableR")}{" "}
              <strong style={{ color: "#2e7d32" }}>{eur(s.revenue_at_risk_recoverable ?? 0)}</strong>
              <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: 4 }}>{t("funnel.revenueRecoverableRHint")}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="ch-card">
        <h2>{t("emailPerf.title")}</h2>
        <p>
          {t("emailPerf.subtitle")}{" "}
          <span style={{ fontSize: 12, color: "#9ca3af" }}>{t("emailPerf.webhookNote")}</span>
        </p>
        <div className="table-wrap table-min-wide">
          <table>
            <thead>
              <tr>
                <th>{t("emailPerf.thSequence")}</th>
                <th>{t("emailPerf.thSent")}</th>
                <th>{t("emailPerf.thOpened")}</th>
                <th>{t("emailPerf.thOpenRate")}</th>
                <th>{t("emailPerf.thClickRate")}</th>
                <th>{t("emailPerf.thRecovered")}</th>
                <th>{t("emailPerf.thRecoveryRate")}</th>
              </tr>
            </thead>
            <tbody>
              {(s.email_performance || []).map((r) => (
                <tr key={r.label || (r.min_cart_value !== undefined ? `€${r.min_cart_value}${r.max_cart_value ? "–€" + r.max_cart_value : "+"}` : "—")}>
                  <td style={{ fontWeight: 600 }}>
                    {r.label || (r.min_cart_value !== undefined ? `€${r.min_cart_value}${r.max_cart_value ? "–€" + r.max_cart_value : "+"}` : "—")}
                  </td>
                  <td className="td-muted">{r.sent}</td>
                  <td className="td-muted">{r.opened || "—"}</td>
                  <td>
                    {r.open_rate > 0 ? (
                      <>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <div className="progress-wrap" style={{ width: 50 }}>
                            <div className="progress-bar" style={{ width: `${r.open_rate}%` }} />
                          </div>
                          <span style={{ fontSize: 13 }}>{r.open_rate}%</span>
                        </div>
                      </>
                    ) : (
                      <span style={{ color: "#9ca3af", fontSize: 13 }}>—</span>
                    )}
                  </td>
                  <td>{r.click_rate > 0 ? `${r.click_rate}%` : <span style={{ color: "#9ca3af", fontSize: 13 }}>—</span>}</td>
                  <td style={{ color: "#2e7d32", fontWeight: 500 }}>{r.recovered}</td>
                  <td>
                    {r.recovery_rate > 0 ? (
                      <span className="ch-status success">{r.recovery_rate}%</span>
                    ) : (
                      <span style={{ color: "#9ca3af", fontSize: 13 }}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="stats-top-products">
        <div className="ch-card">
          <h2>{t("topRecovered.title")}</h2>
          <p>{t("topRecovered.subtitle")}</p>
          {(s.top_recovered || []).length > 0 ? (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{t("topRecovered.thProduct")}</th>
                    <th>{t("topRecovered.thRecovered")}</th>
                    <th>{t("topRecovered.thRevenue")}</th>
                  </tr>
                </thead>
                <tbody>
                  {s.top_recovered.map((p) => (
                    <tr key={p.product_name}>
                      <td style={{ fontWeight: 500 }}>{p.product_name}</td>
                      <td style={{ color: "#2e7d32", fontWeight: 600 }}>{p.recovered}</td>
                      <td style={{ color: "#0A85CF", fontWeight: 500 }}>{eur(p.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty" style={{ padding: 24 }}>
              <div className="empty-icon-svg" aria-hidden>
                {Ic.trophy}
              </div>
              <div>{t("topRecovered.empty")}</div>
            </div>
          )}
        </div>
        <div className="ch-card">
          <h2>{t("topAbandoned.title")}</h2>
          <p>{t("topAbandoned.subtitle")}</p>
          {(s.top_abandoned || []).length > 0 ? (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{t("topAbandoned.thProduct")}</th>
                    <th>{t("topAbandoned.thAbandoned")}</th>
                    <th>{t("topAbandoned.thRecovery")}</th>
                    <th>{t("topAbandoned.thPrice")}</th>
                  </tr>
                </thead>
                <tbody>
                  {s.top_abandoned.map((p) => (
                    <tr key={p.product_name}>
                      <td style={{ fontWeight: 500 }}>{p.product_name}</td>
                      <td className="td-muted">{p.abandoned}</td>
                      <td>
                        {parseFloat(p.recovery_rate) > 0 ? (
                          <span className="ch-status success">{p.recovery_rate}%</span>
                        ) : (
                          <span style={{ color: "#9ca3af", fontSize: 13 }}>—</span>
                        )}
                      </td>
                      <td style={{ color: "#0A85CF" }}>{eur(p.avg_price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty" style={{ padding: 24 }}>
              <div className="empty-icon-svg" aria-hidden>
                {Ic.package}
              </div>
              <div>{t("topAbandoned.empty")}</div>
            </div>
          )}
        </div>
      </div>

      <div className="grid-2">
        <div className="ch-card">
          <h2>{t("activity.title")}</h2>
          <p>{t("activity.subtitle")}</p>
          {(s.activity || []).length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {s.activity.map((a, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "flex-start",
                    padding: "8px 0",
                    borderBottom: i < s.activity.length - 1 ? "1px solid #f3f4f6" : "none",
                  }}
                >
                  <span className="ch-stat-activity-ic">
                    {a.type === "recovered" && a.attribution_channel === "onsite_popup"
                      ? actIcons.recovered_popup
                      : actIcons[a.type] || <span style={{ opacity: 0.4 }}>•</span>}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>
                      {a.type === "recovered" && a.attribution_channel === "onsite_popup"
                        ? t("activity.typePopupOrder")
                        : a.type === "recovered"
                          ? t("activity.typeRecovered")
                          : a.subtype === "reminder_1"
                            ? t("activity.typeR1")
                            : a.subtype === "reminder_2"
                              ? t("activity.typeR2")
                              : t("activity.typeR3")}
                    </div>
                    <div style={{ fontSize: 13, color: "#6b7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {a.name || a.email}
                      {a.value ? ` · ${eur(a.value)}` : ""}
                    </div>
                    {a.detail && (
                      <div
                        style={{
                          fontSize: 12,
                          color: "#9ca3af",
                          fontStyle: "italic",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {`"${a.detail}"`}
                      </div>
                    )}
                  </div>
                  <span style={{ fontSize: 12, color: "#9ca3af", flexShrink: 0, marginTop: 2 }}>
                    {a.ts
                      ? new Date(a.ts).toLocaleDateString(dateLoc, {
                          day: "2-digit",
                          month: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "—"}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty" style={{ padding: 24 }}>
              <div className="empty-icon-svg" aria-hidden>
                {Ic.clipboardList}
              </div>
              <div>{t("activity.empty")}</div>
            </div>
          )}
        </div>
        <div className="ch-card">
          <h2>{t("insights.title")}</h2>
          <p>{t("insights.subtitle")}</p>
          {(s.insights || []).length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {s.insights.map((ins, i) => (
                <div
                  key={i}
                  style={{
                    padding: "10px 14px",
                    background: "#f9fafb",
                    borderRadius: 8,
                    border: "1px solid #e5e5e5",
                    fontSize: 14,
                    lineHeight: 1.5,
                  }}
                >
                  {ins}
                </div>
              ))}
            </div>
          ) : (
            <div className="ch-info">{s.has_enough_data ? t("insights.none") : t("insights.needData")}</div>
          )}
        </div>
      </div>
    </div>
  );
}
